import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";

export interface EntityLimits {
  vehicles: number | null;
  devices: number | null;
  users: number | null;
  geofences: number | null;
  drivers: number | null;
  groups: number | null;
  alerts: number | null;
  reports: number | null;
}

export interface License {
  id: string;
  name: string;
  description: string | null;
  status: "active" | "inactive" | "draft";
  billing_model: "per_unit" | "flat" | "tiered";
  price_per_unit: number | null;
  flat_price: number | null;
  currency: "MXN" | "USD";
  billing_cycle: "monthly" | "quarterly" | "yearly";
  apps: string[];
  features: string[];
  entity_limits: EntityLimits;
  notes: string | null;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface CreateLicenseData {
  name: string;
  description?: string;
  status?: "active" | "inactive" | "draft";
  billing_model?: "per_unit" | "flat" | "tiered";
  price_per_unit?: number;
  flat_price?: number;
  currency?: "MXN" | "USD";
  billing_cycle?: "monthly" | "quarterly" | "yearly";
  apps?: string[];
  features?: string[];
  entity_limits?: Partial<EntityLimits>;
  notes?: string;
}

interface LicensesMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface LicensesStore {
  licenses: License[];
  allLicenses: License[];
  allLicensesLoading: boolean;
  loading: boolean;
  error: string | null;
  meta?: LicensesMeta;
  fetchLicenses: (params?: {
    limit?: number;
    page?: number;
    name?: string;
    status?: string;
    billing_model?: string;
  }) => Promise<void>;
  fetchAllLicenses: (force?: boolean) => Promise<void>;
  fetchLicense: (id: string) => Promise<License>;
  createLicense: (data: CreateLicenseData) => Promise<License>;
  updateLicense: (id: string, data: Partial<CreateLicenseData>) => Promise<License>;
  deleteLicense: (id: string) => Promise<void>;
}

export const useLicensesStore = create<LicensesStore>((set, get) => ({
  licenses: [],
  allLicenses: [],
  allLicensesLoading: false,
  loading: false,
  error: null,
  meta: undefined,

  fetchLicenses: async (params = {}) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/licenseList", {
        params: {
          limit: params.limit ?? 10,
          page: params.page ?? 1,
          ...params,
        },
      });
      set({ licenses: data.data || [], meta: data.meta, loading: false });
    } catch (err: unknown) {
      if (isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },

  fetchAllLicenses: async (force = false) => {
    if (get().allLicensesLoading) return;
    if (!force && get().allLicenses.length > 0) return;

    set({ allLicensesLoading: true });
    try {
      let allData: License[] = [];
      let currentPage = 1;
      let hasMore = true;
      const pageLimit = 100;

      while (hasMore) {
        const { data } = await api.get("/licenseList", {
          params: { limit: pageLimit, page: currentPage },
        });
        const licenses = data.data || [];
        allData = [...allData, ...licenses];
        const totalPages = data.meta?.totalPages || 1;
        hasMore = currentPage < totalPages;
        currentPage++;
      }

      set({ allLicenses: allData, allLicensesLoading: false });
    } catch (err: unknown) {
      set({ error: getError(err), allLicensesLoading: false });
    }
  },

  fetchLicense: async (id: string) => {
    const { data } = await api.get("/license", { params: { id } });
    return data.data;
  },

  createLicense: async (licenseData) => {
    const { data } = await api.post("/license", licenseData);
    return data.data;
  },

  updateLicense: async (id: string, licenseData) => {
    const { data } = await api.put("/license", licenseData, {
      params: { id },
    });
    return data.data;
  },

  deleteLicense: async (id: string) => {
    await api.delete("/license", { params: { id } });
  },
}));
